import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchNotifications,
  readNotification,
  readAllNotifications,
  notificationLinkTarget,
  notificationPriorityBadgeLabel,
} from "../../constants/notifications";
import { smartBack } from "../../constants/nav.js";
import { cn } from "../../utils/themeClasses.js";
import AppButton from "../ui/AppButton.jsx";
import {
  detailCardCompactSecondaryLinkClassName,
  detailPageHeaderNavClassName,
  detailPageHeaderNavActionsClassName,
} from "../../design/tokens.js";

/**
 * Vollseite im Übersicht-Look:
 * - Tabs: Alle / Ungelesen
 * - Gruppierung: Heute, Gestern, Diese Woche, Älter
 * - Kompakte Cards mit Status-Punkt, Link, Aktion "als gelesen"
 */
export default function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "unread" ? "unread" : "all"));

  useEffect(() => {
    const next = searchParams.get("tab") === "unread" ? "unread" : "all";
    setTab((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  const setNotifTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "unread") params.set("tab", "unread");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  const [markingAll, setMarkingAll] = useState(false);
  const unreadExists = useMemo(() => items.some(i => !i.is_read), [items]);
  const unreadCount = useMemo(() => items.filter(i => !i.is_read).length, [items]);
  const rtf = useMemo(() => new Intl.RelativeTimeFormat("de-CH", {
    numeric: "auto"
  }), []);
  const relTime = iso => {
    try {
      const d = new Date(iso);
      const diffMin = Math.round((d.getTime() - Date.now()) / 60000);
      if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute");
      const diffH = Math.round(diffMin / 60);
      if (Math.abs(diffH) < 24) return rtf.format(diffH, "hour");
      const diffD = Math.round(diffH / 24);
      return rtf.format(diffD, "day");
    } catch {
      return "";
    }
  };
  const dtfDate = useMemo(() => new Intl.DateTimeFormat("de-CH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }), []);
  const dtfTime = useMemo(() => new Intl.DateTimeFormat("de-CH", {
    hour: "2-digit",
    minute: "2-digit"
  }), []);
  const fmtDateTime = iso => {
    try {
      const d = new Date(iso);
      return `${dtfDate.format(d)} · ${dtfTime.format(d)}`;
    } catch {
      return "";
    }
  };
  async function load() {
    setLoading(true);
    try {
      const {
        rows
      } = await fetchNotifications({
        limit: 200
      });
      setItems(Array.isArray(rows) ? rows : []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const markOneRead = async id => {
    setItems(prev => prev.map(x => x.id === id ? {
      ...x,
      is_read: true
    } : x));
    try {
      await readNotification(id);
    } catch {}
  };
  const markAllRead = async () => {
    if (markingAll || !unreadExists) return;
    setMarkingAll(true);
    setItems(prev => prev.map(x => ({
      ...x,
      is_read: true
    })));
    try {
      await readAllNotifications();
    } finally {
      setMarkingAll(false);
    }
  };

  /* ───────────── Filter + Gruppierung ───────────── */
  const source = useMemo(() => tab === "unread" ? items.filter(i => !i.is_read) : items, [items, tab]);
  const groups = useMemo(() => {
    const now = new Date();
    const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
    const today = startOfDay(now);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const startOfWeek = new Date(today);
    const day = (today.getDay() + 6) % 7; // Mo=0
    startOfWeek.setDate(today.getDate() - day);
    const out = {
      today: [],
      yesterday: [],
      week: [],
      older: []
    };
    for (const n of source) {
      const d = new Date(n.created_at || n.ts || Date.now());
      if (isSameDay(d, today)) out.today.push(n);else if (isSameDay(d, yesterday)) out.yesterday.push(n);else if (d >= startOfWeek) out.week.push(n);else out.older.push(n);
    }
    return out;
  }, [source]);
  const Section = ({ title, rows }) =>
    !rows.length ? null : (
      <div>
        <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2.5 pl-0.5 font-semibold">{title}</div>
        <div className="space-y-2">
          {rows.map(n => {
            const meta = [];
            if (n?.meta?.name) meta.push(n.meta.name);
            if (n?.meta?.customer_label) meta.push(n.meta.customer_label);
            const openTarget = notificationLinkTarget(n.link);
            const pri = n?.priority || "info";
            const priBadge = notificationPriorityBadgeLabel(pri);
            const evc = Number(n?.event_count) > 1 ? n.event_count : null;
            return (
              <div
                key={n.id}
                className={cn(
                  "rounded-xl border border-default px-3 py-3 sm:px-4 sm:py-3.5 backdrop-blur-sm transition-colors",
                  n.is_read
                    ? "bg-white/[0.02] hover:bg-white/[0.04]"
                    : pri === "action"
                      ? "bg-amber-500/[0.08] hover:bg-amber-500/[0.12]"
                      : pri === "warning"
                        ? "bg-orange-500/[0.08] hover:bg-orange-500/[0.12]"
                        : "bg-blue-500/[0.1] hover:bg-blue-500/[0.14]"
                )}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <span
                    className={cn(
                      "mt-1.5 inline-block w-2 h-2 rounded-full shrink-0",
                      n.is_read ? "bg-white/25" : "bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.55)]"
                    )}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-medium text-white/95 [overflow-wrap:anywhere]">{n.title}</div>
                      {priBadge && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-default text-white/50 shrink-0">
                          {priBadge}
                        </span>
                      )}
                      {evc != null && <span className="text-[10px] text-white/45 shrink-0">×{evc}</span>}
                    </div>
                    {!!meta.length && <div className="text-xs mt-0.5 text-white/45">{meta.join(" — ")}</div>}
                    {n.body && (
                      <div className="text-xs mt-1 text-white/60 leading-snug max-w-full min-w-0 [overflow-wrap:anywhere]">
                        {n.body}
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2 sm:gap-2.5 items-center">
                      {!n.is_read && (
                        <AppButton type="button" variant="primary" size="sm" className="text-xs" onClick={() => markOneRead(n.id)}>
                          Als gelesen
                        </AppButton>
                      )}
                      {openTarget?.type === "router" && (
                        <Link to={openTarget.to} className={detailCardCompactSecondaryLinkClassName}>
                          Öffnen
                        </Link>
                      )}
                      {openTarget?.type === "external" && (
                        <a
                          href={openTarget.href}
                          className={detailCardCompactSecondaryLinkClassName}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Öffnen
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="text-[11px] text-right text-white/45 whitespace-nowrap pl-1 sm:pl-2 shrink-0 pt-0.5">
                    {fmtDateTime(n.created_at)} <span className="opacity-75">· {relTime(n.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );

  /* ───────────── Render (Layout wie Wartungsverträge / Service-Liste: app-frame + zweite Toolbar-Karte) ───────────── */
  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const glass = "app-frame";
  const topbarCls = cn("rounded-2xl px-4 py-3 mb-6 border", glass);
  const filterBarCls = cn("rounded-xl px-2.5 py-2 border mb-6", glass);
  const listCardCls = cn("rounded-2xl border", glass);

  const subtitle = loading
    ? "Lade Daten…"
    : unreadCount > 0
      ? `${items.length} Meldungen · ${unreadCount} ungelesen`
      : `${items.length} ${items.length === 1 ? "Meldung" : "Meldungen"}`;

  return (
    <div className={grad}>
      <div className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <header className={topbarCls}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight">🔔 Benachrichtigungen</h1>
              <p className="mt-1 text-sm text-blue-200/80">{subtitle}</p>
            </div>
            <div className={detailPageHeaderNavActionsClassName}>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                title="Zu Meine Aufgaben"
                onClick={() => navigate("/meine-aufgaben")}
              >
                Meine Aufgaben
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                title="Zurück"
                onClick={() => smartBack(navigate, ["/", "/dashboard"])}
              >
                Zurück
              </AppButton>
              <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} title="Zum Dashboard" onClick={() => navigate("/")}>
                Dashboard
              </AppButton>
            </div>
          </div>
        </header>

        <div className={filterBarCls}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex rounded-xl overflow-hidden border border-white/[0.1] bg-white/[0.04] p-0.5">
              <AppButton
                type="button"
                variant="ghost"
                onClick={() => setNotifTab("all")}
                className={cn(
                  "px-3 py-2 text-sm rounded-lg border-0 shadow-none !min-h-0 h-9",
                  tab === "all" ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"
                )}
              >
                Alle
              </AppButton>
              <AppButton
                type="button"
                variant="ghost"
                onClick={() => setNotifTab("unread")}
                className={cn(
                  "px-3 py-2 text-sm rounded-lg border-0 shadow-none !min-h-0 h-9 -ml-px",
                  tab === "unread" ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"
                )}
              >
                Ungelesen
              </AppButton>
            </div>

            <AppButton
              type="button"
              variant="secondary"
              size="lg"
              onClick={markAllRead}
              disabled={!unreadExists || markingAll}
              className={cn(detailPageHeaderNavClassName, (!unreadExists || markingAll) && "opacity-50 cursor-not-allowed")}
            >
              {markingAll ? "Markiere…" : "Alle als gelesen"}
            </AppButton>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-2">
            <div className={cn("h-24 animate-pulse rounded-2xl border opacity-60", glass)} />
            <div className={cn("h-24 animate-pulse rounded-2xl border opacity-60", glass)} />
            <div className={cn("h-24 animate-pulse rounded-2xl border opacity-60", glass)} />
          </div>
        ) : source.length === 0 ? (
          <div className={cn(listCardCls, "p-10 text-center text-white/55 text-sm")}>Keine Benachrichtigungen.</div>
        ) : (
          <div className="space-y-8">
            <Section title="Heute" rows={groups.today} />
            <Section title="Gestern" rows={groups.yesterday} />
            <Section title="Diese Woche" rows={groups.week} />
            <Section title="Älter" rows={groups.older} />
          </div>
        )}

        <div className="h-6 md:h-8" />
      </div>
    </div>
  );
}